import { chakra, Flex, HStack, Icon, Text } from '@chakra-ui/react'
import { ChevronDown, ChevronUp, User } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import {
  convertCallDurationNoMilliseconds,
  phoneFormatter,
} from '~/utils/formatter'
import { getContactFullName } from '~/utils/phoneHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { FontWeight, textStyles } from '@mangovoice/design-system'
import {
  CallQueueItem,
  isPreConnectedStatus,
  PhoneCallStatus,
} from '@mangovoice/types'

export interface CallItemHeaderProps {
  callItem: CallQueueItem
  isExpanded: boolean
  onToggle?: () => void
}

export const CallItemHeader: React.FC<CallItemHeaderProps> = ({
  callItem,
  isExpanded,
  onToggle,
}) => {
  const { t } = useTranslation()
  const [duration, setDuration] = useState<string>('0:00')

  // Prioritize contact info when available
  const displayName = callItem?.isPageOrIntercom
    ? t('popup.callItem.pageIntercom')
    : getContactFullName(callItem?.contactInfo) ||
      callItem?.name ||
      t('popup.callItem.unknownCaller')
  const displayNumber = callItem?.isPageOrIntercom
    ? callItem?.extensionNumber
      ? `Ext ${callItem.extensionNumber}`
      : ''
    : callItem?.contactInfo?.phone_number ||
      callItem?.number ||
      t('popup.callItem.unknownNumber')

  const isConnected = callItem.status === PhoneCallStatus.CONNECTED
  const isOnHold = callItem.isOnHold ?? false

  // Calculate duration
  useEffect(() => {
    const ringStartTime = callItem?.createdAt || callItem?.startTime

    if (!ringStartTime) {
      setDuration('0:00')
      return
    }

    const updateDuration = () => {
      const now = new Date()
      const startTime = new Date(ringStartTime!)
      const durationMs = now.getTime() - startTime.getTime()
      const durationSeconds = Math.floor(durationMs / 1000)

      if (durationSeconds >= 0) {
        setDuration(convertCallDurationNoMilliseconds(durationSeconds))
      }
    }

    updateDuration()
    const interval = setInterval(updateDuration, 1000)

    return () => clearInterval(interval)
  }, [callItem?.createdAt, callItem?.startTime, callItem?.callUuid])

  // Get SIP connection status to determine if we can track hold status
  const [sipConnected] = useSharedVariable<boolean>('SipConnected', false)

  const getStatusDisplay = () => {
    if (!callItem.status) return null

    let statusLabel: string

    if (isOnHold) {
      statusLabel = t('callStatus.onHold')
    } else if (isConnected && !sipConnected) {
      statusLabel = t('callStatus.deskPhone')
    } else if (callItem.status === PhoneCallStatus.CONNECTING) {
      statusLabel = t('callStatus.dialing')
    } else if (isPreConnectedStatus(callItem.status)) {
      statusLabel = t('callStatus.ringing')
    } else if (isConnected) {
      statusLabel = t('callStatus.connected')
    } else {
      statusLabel = t('callStatus.connecting')
    }

    return `${statusLabel} [${duration}]`
  }

  const getStatusDisplayColor = () => {
    if (isOnHold) return 'warning'
    if (isConnected) return 'green.solid'
    return 'fg.muted'
  }

  const avatarSize = isExpanded ? 12 : 10
  const iconSize = isExpanded ? 24 : 20
  const nameStyle = isExpanded
    ? textStyleKeys.Body1_Bold
    : textStyleKeys.Body2_Bold

  return (
    <HStack gap={3} align="center" flex={1}>
      <Flex
        align="center"
        justify="center"
        w={avatarSize}
        h={avatarSize}
        borderRadius="full"
        bg={isExpanded ? 'bg.panel' : 'bg.subtle'}
        color="fg.subtle"
        flexShrink={0}
      >
        <User width={iconSize} height={iconSize} />
      </Flex>

      <Flex direction="column" flex={1} minW={0} align="flex-start" gap={1}>
        <Text
          {...textStyles[nameStyle]}
          color="fg.default"
          lineClamp={1}
          textAlign="left"
        >
          {displayName}
        </Text>
        {displayNumber && (
          <HStack gap={2} align="center">
            <Text
              {...textStyles[textStyleKeys.Body3]}
              color="fg.muted"
              lineClamp={1}
              textAlign="left"
            >
              {callItem?.isPageOrIntercom
                ? displayNumber
                : phoneFormatter(displayNumber)}
            </Text>
          </HStack>
        )}
      </Flex>

      <HStack gap={1}>
        {getStatusDisplay() && (
          <HStack gap={1}>
            <Text
              {...textStyles[textStyleKeys.Body3]}
              fontWeight={isExpanded ? FontWeight.SemiBold : FontWeight.Normal}
              color={getStatusDisplayColor()}
            >
              {getStatusDisplay()}
            </Text>
          </HStack>
        )}

        {onToggle && (
          <chakra.button
            type="button"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="sm"
            cursor="pointer"
            aria-label={t('popup.callItem.callDetails', 'Call details')}
            aria-expanded={isExpanded}
            onClick={(event) => {
              event.stopPropagation()
              onToggle()
            }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'brand.solid',
              outlineOffset: '2px',
            }}
          >
            <Icon as={isExpanded ? ChevronUp : ChevronDown} boxSize={5} />
          </chakra.button>
        )}
      </HStack>
    </HStack>
  )
}
